import { Method } from 'axios';

import { Messages } from '../../codegen/Localize';
import { openReactConfirmationDialog, ReactConfirmationDialogOptions } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '../../commonInfra/L10n/L10n';
import { ToasterService, ToastType } from '../../commonInfra/services/ToasterService';
import { makeRestApiRequest, RestApiRequestConfig } from '../../httpClient/RestApiService';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ContentFiltersKeys } from '../../spacesPlatform/models/ContentFilters';
import { isPersonalSpace, isProjectSpace } from '../../spacesPlatform/state/SpaceInfo';
import { DiscardDraftRequestedAction } from '../actions/DiscardDraftAction';
import { FetchDraftByReferenceIdRequestedAction, FetchDraftsRequestedAction } from '../actions/DraftsActions';
import { ContainerType, Draft, DraftCount, DraftReferenceType, DraftRequestBody } from '../types/DraftsTypes';
import { DraftUtils } from '../utils/DraftUtils';
import { WorkbookDraftDraftRestApiVersion } from './DraftsRestVersion';

export const DraftsRequests = {
  getDraftsRequest,
  getDraftCountRequest,
  getDraftByReferenceIdRequest,
  getDraftsRequestQueryParams,
  deleteDraft,
  deleteDrafts,
  discardDraftRequest,
  callViaApiService,
};

export type DraftRequestConfigType = Omit<RestApiRequestConfig<DraftRequestBody>, 'requestBody' | 'headers' | 'queryParams'>;

export function getRequestConfig(httpMethod: Method, endpoint?: string): DraftRequestConfigType {
  return {
    restApiVersion: WorkbookDraftDraftRestApiVersion,
    httpMethod,
    endpoint: endpoint ? `workbookdrafts/${endpoint}`: 'workbookdrafts'
  };
}

export interface GetDraftsByIdRequestParams {
  parentRevisionId: string;
}

export interface GetDraftsRequestQueryParams {
  containerLuid: string;
  containerType: ContainerType;
  createdAfter: string;
  createdBefore: string;
  draftReferenceType: DraftReferenceType;
  name: string;
  updatedAfter: string;
  updatedBefore: string;
}

export interface PutRenameDraftRequestParams {
  name: string;
}

export type QueryParams = GetDraftsRequestQueryParams | GetDraftsByIdRequestParams | PutRenameDraftRequestParams;

export interface DiscardDraftRequestParams {
  draftLuid: string;
}

export async function getDraftByReferenceIdRequest(action: FetchDraftByReferenceIdRequestedAction): Promise<Draft> {
  const { parentRevisionId } = action.data;
  const referenceId = action.referenceId;

  const config = getRequestConfig('GET', `findDraftByReferenceId/${referenceId}`);
  const queryParams = parentRevisionId ? {parentRevisionId} : undefined;

  return DraftsRequests.callViaApiService<Draft>(config, queryParams);
}

export async function getDraftsRequest(action: FetchDraftsRequestedAction): Promise<Draft[]> {
  const isRequestForProjectWorkbookCounts = DraftUtils.shouldProjectsIncludeUnpublishedWorkbookCount(action.contentTypes);

  if (!isRequestForProjectWorkbookCounts && !action.contentTypes.includes('workbook')) {
    // Fetch drafts when project is the only selected content type or workbook is a selected content type; otherwise, return empty array
    return new Promise<Draft[]>(resolve => resolve([]));
  }

  const shouldRequestUnpublishedWorkbookDrafts = isRequestForProjectWorkbookCounts || DraftUtils.shouldIncludeUnpublishedWorkbookDrafts(action.filters, action.spaceInfo);
  return getRequest<Draft[]>(action, shouldRequestUnpublishedWorkbookDrafts);
}

export async function getDraftCountRequest(action: FetchDraftsRequestedAction): Promise<DraftCount> {
  const shouldRequestUnpublishedWorkbookDrafts = DraftUtils.shouldIncludeUnpublishedWorkbookDraftCount(action.filters, action.spaceInfo);
  return getRequest<DraftCount>(action, shouldRequestUnpublishedWorkbookDrafts, 'count');
}

async function getRequest<T>(
  action: FetchDraftsRequestedAction,
  shouldRequestUnpublishedWorkbookDrafts: boolean,
  endpoint?: string
): Promise<T> {
  const config = getRequestConfig('GET', endpoint);
  const queryParams = DraftsRequests.getDraftsRequestQueryParams(action, shouldRequestUnpublishedWorkbookDrafts);

  return DraftsRequests.callViaApiService<T>(config, queryParams);
}

export async function deleteDraft(draftLuid: string): Promise<IResult> {
  const config = getRequestConfig('DELETE', draftLuid);

  return new Promise<IResult>((resolve) => {
    DraftsRequests.callViaApiService(config)
      .catch(error => {
        resolve({ errors: [{ code: error.status, id: draftLuid }] });
      })
      .then(() => {
        resolve({ errors: [] });
      });
  });
}

export interface BatchDeleteResponse {
  draftsFailedToDelete: string[];
}

export async function deleteDrafts(draftLuids: string[]): Promise<IResult> {
  let errors: IError[] = [];

  if (!draftLuids || draftLuids.length === 0) {
    return new Promise<IResult>(resolve => resolve({ errors }));
  } else if (draftLuids.length === 1) {
    return DraftsRequests.deleteDraft(draftLuids[0]);
  }

  const config = getRequestConfig('POST', 'batchDelete');

  return new Promise<IResult>((resolve) => {
    DraftsRequests.callViaApiService(config, undefined, draftLuids)
      .catch(error => {
        draftLuids.forEach(draftLuid => {
          errors.push({ code: error.status, id: draftLuid });
        });
      })
      .then((response: BatchDeleteResponse) => {
        if (response) {
          response.draftsFailedToDelete.forEach(draftLuid => {
            errors.push({ code: 404, id: draftLuid });
          });
        }
      })
      .finally(() => {
        resolve({ errors });
      });
  });
}

export function discardDraftRequest(action: DiscardDraftRequestedAction): Promise<{}> {
  const initialModalInstance = openReactConfirmationDialog<string, {}>(discardDraftDialogOptions(action));
  return initialModalInstance.result;
}

// Separated for unit testing
export function discardDraftDialogOptions(action: DiscardDraftRequestedAction): ReactConfirmationDialogOptions<string, {}> {
  let dialogBody: string = Messages.confirmDeleteDraftWarning({
    DRAFT_CONTENT_TYPE: action.contentType,
    DRAFT_NAME: L10n.elideIfLong(action.name)
  });

  function onClickDiscard(draftLuid: string): PromiseLike<{}> {
    const config = getRequestConfig('DELETE', draftLuid);

    const response = DraftsRequests.callViaApiService(config);
    response.catch(() => {
      ToasterService.instance.pop({type: ToastType.Error, text: Messages.deleteDraftError()});
    });
    return response;
  }

  return {
    title: Messages.deleteMessage(),
    message: dialogBody,
    paramsForAction: action.draftLuid,
    actionName: Messages.deleteDraft(),
    action: onClickDiscard,
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  };
}

export async function renameDraft(draftLuid: string, name: string): Promise<IResult> {
  const queryParams = { name };
  const config = getRequestConfig('PUT', `${draftLuid}/rename`);

  return new Promise<IResult>((resolve) => {
    DraftsRequests.callViaApiService(config, queryParams)
      .catch(error => {
        resolve({ errors: [{ code: error.status, id: draftLuid }] });
      })
      .then(() => {
        resolve({ errors: [] });
      });
  });
}

export function callViaApiService<ReturnType>(
  requestConfig: DraftRequestConfigType,
  queryParams?: QueryParams,
  body?: DraftRequestBody,
  headers?: {[index: string]: string},
) {
  const config: RestApiRequestConfig<DraftRequestBody, QueryParams> = {
    ...requestConfig,
    requestBody: body,
    headers,
    queryParams,
  };

  return makeRestApiRequest<DraftRequestBody, ReturnType>(config);
}

// exported for testing
export function getDraftsRequestQueryParams(
  action: FetchDraftsRequestedAction,
  shouldRequestUnpublishedWorkbookDrafts: Boolean
): GetDraftsRequestQueryParams {
  const { filters } = action;
  const containerLuid = isProjectSpace(action.spaceInfo) ? action.spaceInfo.item?.luid : undefined;
  const containerType = isPersonalSpace(action.spaceInfo) ? ContainerType.PersonalSpace : undefined;
  const createdAfter = DraftUtils.getFilterValue(filters, ContentFiltersKeys.createdAfter);
  const createdBefore = DraftUtils.getFilterValue(filters, ContentFiltersKeys.createdBefore);
  const draftReferenceType = !shouldRequestUnpublishedWorkbookDrafts ? DraftReferenceType.PublishedWorkbook : undefined;
  const name = DraftUtils.getFilterValue(filters, ContentFiltersKeys.search);
  const updatedAfter = DraftUtils.getFilterValue(filters, ContentFiltersKeys.modifiedAfter);
  const updatedBefore = DraftUtils.getFilterValue(filters, ContentFiltersKeys.modifiedBefore);

  return {
    ...(containerLuid ? { containerLuid } : undefined),
    ...(containerType ? { containerType } : undefined),
    ...(createdAfter ? { createdAfter } : undefined),
    ...(createdBefore ? { createdBefore } : undefined),
    ...(draftReferenceType ? { draftReferenceType } : undefined),
    ...(name ? { name } : undefined),
    ...(updatedAfter ? { updatedAfter } : undefined),
    ...(updatedBefore ? { updatedBefore } : undefined),
  };
}
